import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Offcanvas, Accordion } from 'react-bootstrap';
import { getDocumentationGroupedByType, searchDocumentation } from '../../models/documentationModel';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';

/**
 * DocumentationHandle - A vertical side-tab handle that shows context-aware documentation
 * Displays different types of documentation (pages, tooltips, walkthroughs, FAQs)
 * Only visible when there is documentation for the current page
 */

// Type configurations with icons and labels
const DOC_TYPES = {
  page: {
    label: 'Documentation',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    color: '#27ae60'
  },
  tooltip: {
    label: 'Quick Tips',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: '#3498db'
  },
  walkthrough: {
    label: 'Walkthroughs',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    color: '#9b59b6'
  },
  faq: {
    label: 'FAQs',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: '#e67e22'
  }
};

function DocumentationHandle() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isIT } = useAuth();
  const [show, setShow] = useState(false);
  const [groupedDocs, setGroupedDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('page');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [hasDocumentation, setHasDocumentation] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [currentWalkthroughStep, setCurrentWalkthroughStep] = useState(0);

  // Fetch documentation when path changes
  useEffect(() => {
    fetchDocumentation();
  }, [location.pathname]);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      setSelectedDoc(null);
      setExpandedFaq(null);
      setCurrentWalkthroughStep(0);
      
      // Fetch all documentation types grouped
      const response = await getDocumentationGroupedByType(location.pathname, false);
      
      if (response.status === 1 || response.status === 'success') {
        const docs = response.data?.documentation;
        const hasContent = response.data?.hasContent || false;
        
        if (docs && hasContent) {
          setGroupedDocs(docs);
          setHasDocumentation(true);
          
          // Auto-select first available tab with content
          const availableTypes = ['page', 'tooltip', 'walkthrough', 'faq'];
          for (const type of availableTypes) {
            if (docs[type]?.length > 0) {
              setActiveTab(type);
              if (type === 'page' && docs.page.length > 0) {
                setSelectedDoc(docs.page[0]);
              }
              break;
            }
          }
        } else {
          setGroupedDocs(null);
          setHasDocumentation(false);
        }
      } else {
        setGroupedDocs(null);
        setHasDocumentation(false);
      }
    } catch (error) {
      console.error('Error fetching documentation:', error);
      setGroupedDocs(null);
      setHasDocumentation(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      setSearching(true);
      const response = await searchDocumentation(term);
      if (response.status === 1 || response.status === 'success') {
        setSearchResults(response.data?.documentation || []);
      }
    } catch (error) {
      console.error('Error searching documentation:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        handleSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearch]);

  const handleNavigateToDoc = (docId) => {
    navigate(`/dashboard/documentation/view/${docId}`);
    setShow(false);
  };

  const handleCreateDocumentation = (type = 'page') => {
    navigate(`/dashboard/documentation/new?path=${encodeURIComponent(location.pathname)}&type=${type}`);
    setShow(false);
  };

  const handleEditDocumentation = (docId) => {
    navigate(`/dashboard/documentation/edit/${docId}`);
    setShow(false);
  };

  // Helper to get doc field - handles both doc_id/id formats
  const getDocField = (doc, field) => {
    return doc?.[`doc_${field}`] || doc?.[field];
  };

  // Exclude main dashboard from documentation handle
  const isMainDashboard = location.pathname === '/dashboard' || location.pathname === '/dashboard/';

  // Only render if there's documentation for this page (and not main dashboard)
  if (loading || !hasDocumentation || isMainDashboard) {
    return null;
  }

  const counts = groupedDocs?.counts || {};
  const availableTabs = ['page', 'tooltip', 'walkthrough', 'faq'].filter(type => counts[type] > 0);

  // Render tooltips as small info cards
  const renderTooltips = () => {
    const tooltips = groupedDocs?.tooltip || [];
    if (tooltips.length === 0) return <EmptyState type="tooltip" />;
    
    return (
      <div className="space-y-3 p-4 overflow-y-auto h-full">
        {tooltips.map((tip) => (
          <div 
            key={getDocField(tip, 'id')}
            className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg"
          >
            <div className="flex items-start gap-2">
              <span className="text-blue-500 flex-shrink-0 mt-0.5">
                {DOC_TYPES.tooltip.icon}
              </span>
              <div className="flex-1">
                <h4 className="font-medium text-blue-800 text-sm">{getDocField(tip, 'title')}</h4>
                {getDocField(tip, 'summary') && (
                  <p className="text-blue-700 text-sm mt-1">{getDocField(tip, 'summary')}</p>
                )}
                {getDocField(tip, 'content') && (
                  <div 
                    className="prose prose-sm max-w-none text-blue-700 mt-2 prose-headings:text-base prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:text-sm prose-li:text-sm"
                    dangerouslySetInnerHTML={{ __html: getDocField(tip, 'content') }}
                  />
                )}
              </div>
              {isIT && isIT() && (
                <button
                  onClick={() => handleEditDocumentation(getDocField(tip, 'id'))}
                  className="text-blue-500 hover:text-blue-700 p-1"
                  title="Edit"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render walkthroughs as step-by-step guides
  const renderWalkthroughs = () => {
    const walkthroughs = groupedDocs?.walkthrough || [];
    if (walkthroughs.length === 0) return <EmptyState type="walkthrough" />;
    
    const currentWalkthrough = walkthroughs[0]; // Show first walkthrough by default
    
    // Parse content into steps (split by <hr> or numbered lists)
    const parseSteps = (content) => {
      if (!content) return [{ content: '' }];
      
      // Try to split by horizontal rules or step markers
      const stepPatterns = [
        /<hr\s*\/?>/gi,
        /(?=<h[1-6]>Step\s+\d+)/gi,
        /(?=<p><strong>Step\s+\d+)/gi
      ];
      
      for (const pattern of stepPatterns) {
        const parts = content.split(pattern).filter(p => p.trim());
        if (parts.length > 1) {
          return parts.map(p => ({ content: p.trim() }));
        }
      }
      
      // If no steps found, return as single step
      return [{ content }];
    };
    
    const steps = parseSteps(getDocField(currentWalkthrough, 'content'));
    
    return (
      <div className="flex flex-col h-full">
        {/* Walkthrough selector if multiple */}
        {walkthroughs.length > 1 && (
          <div className="px-4 pt-4">
            <select 
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={0}
              onChange={(e) => setCurrentWalkthroughStep(0)}
            >
              {walkthroughs.map((wt, idx) => (
                <option key={getDocField(wt, 'id')} value={idx}>
                  {getDocField(wt, 'title')}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Header */}
        <div className="px-3 pt-3 pb-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-purple-800 text-sm">{getDocField(currentWalkthrough, 'title')}</h3>
            {isIT && isIT() && (
              <button
                onClick={() => handleEditDocumentation(getDocField(currentWalkthrough, 'id'))}
                className="text-purple-500 hover:text-purple-700 p-1"
                title="Edit"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Progress indicator - compact */}
        <div className="px-3 py-1">
          <div className="flex items-center gap-0.5">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  idx <= currentWalkthroughStep ? 'bg-purple-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 text-center">
            Step {currentWalkthroughStep + 1} of {steps.length}
          </p>
        </div>
        
        {/* Current step content */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          <div className="bg-purple-50 rounded-lg p-3">
            <div 
              className="prose prose-sm max-w-none prose-purple prose-headings:text-base prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:text-sm prose-li:text-sm"
              dangerouslySetInnerHTML={{ __html: steps[currentWalkthroughStep]?.content || '' }}
            />
          </div>
          
          {/* Screenshots for this step */}
          {currentWalkthrough.screenshots?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {currentWalkthrough.screenshots.map((screenshot) => (
                <img
                  key={screenshot.id || screenshot.screenshot_id}
                  src={`${API_BASE_URL}/${screenshot.filepath || screenshot.screenshot_filename}`}
                  alt={screenshot.caption || 'Step screenshot'}
                  className="w-full h-20 object-cover rounded border cursor-pointer hover:opacity-90"
                  onClick={() => window.open(`${API_BASE_URL}/${screenshot.filepath || screenshot.screenshot_filename}`, '_blank')}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Navigation buttons - compact */}
        <div className="px-3 py-2 border-t flex justify-between">
          <button
            onClick={() => setCurrentWalkthroughStep(prev => Math.max(0, prev - 1))}
            disabled={currentWalkthroughStep === 0}
            className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-100"
          >
            ← Previous
          </button>
          <button
            onClick={() => setCurrentWalkthroughStep(prev => Math.min(steps.length - 1, prev + 1))}
            disabled={currentWalkthroughStep === steps.length - 1}
            className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700"
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  // Render FAQs as accordion
  const renderFaqs = () => {
    const faqs = groupedDocs?.faq || [];
    if (faqs.length === 0) return <EmptyState type="faq" />;
    
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <Accordion activeKey={expandedFaq}>
          {faqs.map((faq, idx) => (
            <Accordion.Item 
              key={getDocField(faq, 'id')} 
              eventKey={getDocField(faq, 'id').toString()}
              className="border-b border-gray-200"
            >
              <Accordion.Header 
                onClick={() => setExpandedFaq(
                  expandedFaq === getDocField(faq, 'id').toString() 
                    ? null 
                    : getDocField(faq, 'id').toString()
                )}
                className="!bg-transparent"
              >
                <div className="flex items-center gap-2 w-full pr-2">
                  <span className="text-orange-500">{DOC_TYPES.faq.icon}</span>
                  <span className="flex-1 text-left font-medium text-gray-800">
                    {getDocField(faq, 'title')}
                  </span>
                  {isIT && isIT() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDocumentation(getDocField(faq, 'id'));
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Edit"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body className="bg-orange-50/50">
                {getDocField(faq, 'summary') && (
                  <p className="text-sm text-orange-700 mb-2 italic">{getDocField(faq, 'summary')}</p>
                )}
                <div 
                  className="prose prose-sm max-w-none prose-headings:text-base prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:text-sm prose-li:text-sm"
                  dangerouslySetInnerHTML={{ __html: getDocField(faq, 'content') }}
                />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };

  // Render documentation pages
  const renderPages = () => {
    const pages = groupedDocs?.page || [];
    if (pages.length === 0) return <EmptyState type="page" />;
    
    return (
      <div className="flex flex-col h-full">
        {/* Page selector if multiple */}
        {pages.length > 1 && (
          <div className="px-3 py-2 border-b bg-gray-50 flex flex-wrap gap-1">
            {pages.map((page) => (
              <button
                key={getDocField(page, 'id')}
                onClick={() => setSelectedDoc(page)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  getDocField(selectedDoc, 'id') === getDocField(page, 'id')
                    ? 'bg-[#27ae60] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {getDocField(page, 'title')}
              </button>
            ))}
          </div>
        )}
        
        {selectedDoc ? (
          <>
            {/* Document Header */}
            <div className="px-3 py-2 border-b bg-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">{getDocField(selectedDoc, 'title')}</h3>
                  <p className="text-xs text-gray-500">
                    Path: {getDocField(selectedDoc, 'path')}
                  </p>
                </div>
                {isIT && isIT() && (
                  <button
                    onClick={() => handleEditDocumentation(getDocField(selectedDoc, 'id'))}
                    className="text-[#27ae60] hover:text-[#219a52] p-1"
                    title="Edit Documentation"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              <div 
                className="prose prose-sm max-w-none prose-headings:text-base prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:text-sm prose-li:text-sm"
                dangerouslySetInnerHTML={{ __html: getDocField(selectedDoc, 'content') }}
              />

              {/* Screenshots */}
              {selectedDoc.screenshots && selectedDoc.screenshots.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Screenshots</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedDoc.screenshots.map((screenshot) => (
                      <div key={screenshot.id || screenshot.screenshot_id} className="relative group">
                        <img
                          src={`${API_BASE_URL}/${screenshot.filepath || screenshot.screenshot_filename}`}
                          alt={screenshot.caption || 'Screenshot'}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(`${API_BASE_URL}/${screenshot.filepath || screenshot.screenshot_filename}`, '_blank')}
                        />
                        {screenshot.caption && (
                          <p className="text-xs text-gray-500 mt-1 text-center">{screenshot.caption}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Video */}
              {getDocField(selectedDoc, 'video_url') && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Video Tutorial</h4>
                  <a 
                    href={getDocField(selectedDoc, 'video_url')} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#27ae60] hover:underline"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Watch Video Tutorial
                  </a>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-3 border-t bg-gray-50 flex justify-between items-center flex-shrink-0">
              <button
                onClick={() => handleNavigateToDoc(getDocField(selectedDoc, 'id'))}
                className="text-sm text-[#27ae60] hover:text-[#219a52] font-medium"
              >
                Open Full View →
              </button>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <p>Select a documentation page</p>
          </div>
        )}
      </div>
    );
  };

  // Empty state component
  const EmptyState = ({ type }) => (
    <div className="p-8 text-center text-gray-500">
      <div className="mb-3 text-gray-300">{DOC_TYPES[type].icon}</div>
      <p className="text-sm">No {DOC_TYPES[type].label.toLowerCase()} available for this page</p>
      {isIT && isIT() && (
        <button
          onClick={() => handleCreateDocumentation(type)}
          className="mt-3 text-sm text-[#27ae60] hover:underline"
        >
          + Add {DOC_TYPES[type].label}
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Vertical Side Tab Handle - Green color, positioned below center */}
      <div className="fixed right-0 z-50" style={{ top: 'calc(50% + 60px)' }}>
        <button 
          className="bg-[#27ae60] text-white px-1.5 py-3 rounded-l-xl shadow-2xl hover:bg-[#219a52] transition-all duration-300 border-l border-t border-b border-white/20 flex flex-col items-center justify-center gap-1"
          onClick={() => setShow(true)}
          title="View Documentation"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {counts.total > 0 && (
            <span className="text-xs font-bold">{counts.total}</span>
          )}
        </button>
      </div>

      {/* Documentation Panel - Offcanvas from right */}
      <Offcanvas 
        show={show} 
        onHide={() => setShow(false)} 
        placement="end"
        className="w-[500px]"
        style={{ maxWidth: '90vw' }}
      >
        <Offcanvas.Header closeButton className="bg-[#27ae60] text-white py-2">
          <Offcanvas.Title className="flex items-center gap-2 flex-1">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span className="text-sm font-medium">Documentation</span>
            {isIT && isIT() && (
              <button
                onClick={() => handleCreateDocumentation(activeTab)}
                className="ml-auto mr-2 bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
                title={`Add ${DOC_TYPES[activeTab]?.label || 'Documentation'}`}
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add
              </button>
            )}
          </Offcanvas.Title>
        </Offcanvas.Header>
        
        <Offcanvas.Body className="p-0 d-flex flex-column" style={{ height: 'calc(100vh - 48px)' }}>
          {/* Search Bar */}
          <div className="px-3 py-2 border-b bg-gray-50 flex-shrink-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27ae60] focus:border-transparent"
              />
              <svg className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-[#27ae60] border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && searchResults.length > 0 && (
            <div className="border-b flex-shrink-0">
              <div className="px-3 py-2 bg-gray-100 text-sm font-medium text-gray-600">
                Search Results ({searchResults.length})
              </div>
              <div className="max-h-48 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={getDocField(result, 'id')}
                    onClick={() => {
                      handleNavigateToDoc(getDocField(result, 'id'));
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">
                        {DOC_TYPES[getDocField(result, 'type') || 'page']?.icon}
                      </span>
                      <div>
                        <div className="font-medium text-gray-800">{getDocField(result, 'title')}</div>
                        <div className="text-xs text-gray-500">{getDocField(result, 'path')}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {searchTerm && searchResults.length === 0 && !searching && (
            <div className="px-4 py-3 text-sm text-gray-500 border-b flex-shrink-0">
              No results found for "{searchTerm}"
            </div>
          )}

          {/* Type Tabs - Compact */}
          {!searchTerm && availableTabs.length > 1 && (
            <div className="flex border-b flex-shrink-0 bg-gray-50">
              {availableTabs.map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setActiveTab(type);
                    if (type === 'page' && groupedDocs?.page?.length > 0) {
                      setSelectedDoc(groupedDocs.page[0]);
                    }
                  }}
                  className={`flex-1 px-2 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors border-b-2 ${
                    activeTab === type
                      ? 'border-[#27ae60] text-[#27ae60] bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  title={DOC_TYPES[type].label}
                >
                  <span className="h-4 w-4" style={{ color: activeTab === type ? DOC_TYPES[type].color : undefined }}>
                    {DOC_TYPES[type].icon}
                  </span>
                  <span className={`px-1 py-0.5 rounded text-xs ${
                    activeTab === type ? 'bg-[#27ae60] text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {counts[type]}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {!searchTerm && activeTab === 'page' && renderPages()}
            {!searchTerm && activeTab === 'tooltip' && renderTooltips()}
            {!searchTerm && activeTab === 'walkthrough' && renderWalkthroughs()}
            {!searchTerm && activeTab === 'faq' && renderFaqs()}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default DocumentationHandle;
